/**
 * /trips/:id — Trip Detail
 *
 * One page that collapses everything a dispatcher or on-the-bus crew
 * needs to run a multi-day Bus Events trip:
 *   • Header   — trip name, date span, status badge, cancel/uncancel action
 *   • Timeline — day-grouped stops with arrival/leave times, extended
 *                stays, overnight markers, live OSRM leg durations
 *   • Map      — all stops numbered on an OSM canvas, polyline per day
 *   • Crew     — driver + hosts + lead_hosts contact cards (mailto deep-links)
 *
 * Access: Admin & Dispatcher always; assigned crew (driver/host/lead_host)
 * can also view. Enforced server-side by GET /api/trips/:id — this page
 * just handles the UX fallout (403 → friendly message).
 *
 * Cancel semantics: soft-cancel cascades every requested/scheduled
 * child bus_tasting to cancelled; completed ones are preserved. The
 * cancel action is Admin/Dispatcher-only (surfaced via trip.can_cancel).
 * Un-cancel is available on the same page when status === "cancelled".
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Polyline, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ArrowLeft,
  Bus,
  Calendar,
  CheckCircle2,
  Clock,
  ExternalLink,
  Loader2,
  MapPin,
  Mail,
  Moon,
  Pencil,
  RefreshCcw,
  Trash2,
  Truck,
  UserCheck,
  Users,
  XCircle,
  AlertTriangle,
  Briefcase,
} from "lucide-react";
import { fetchTrip, cancelTrip, uncancelTrip } from "../lib/api";
import { useAuth } from "../lib/AuthProvider";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

// ---------------- helpers ----------------

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const fmtTime = (hhmm) => {
  if (!hhmm) return "—";
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const hh12 = ((h + 11) % 12) + 1;
  return `${hh12}:${String(m).padStart(2, "0")} ${period}`;
};

const fmtMinutes = (m) => {
  if (m == null) return "—";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
};

const dateRangeLabel = (start, end) => {
  if (!start) return "—";
  if (!end || start === end) return fmtDate(start);
  return `${fmtDate(start)} → ${fmtDate(end)}`;
};

// Numbered map pin as a divIcon so we don't ship extra PNG assets.
const makeNumberedIcon = (num, tone) =>
  L.divIcon({
    className: "",
    html: `<div style="
      display:flex;align-items:center;justify-content:center;
      width:28px;height:28px;border-radius:50%;
      background:${tone.bg};border:2px solid ${tone.border};
      color:${tone.text};font-weight:700;font-size:12px;
      box-shadow:0 2px 6px rgba(0,0,0,.4);
      font-family:ui-monospace,monospace;
    ">${num}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });

const DAY_TONES = [
  { bg: "#10b981", border: "#064e3b", text: "#ecfdf5" },  // emerald
  { bg: "#8b5cf6", border: "#2e1065", text: "#f5f3ff" },  // violet
  { bg: "#f59e0b", border: "#78350f", text: "#fffbeb" },  // amber
  { bg: "#06b6d4", border: "#164e63", text: "#ecfeff" },  // cyan
  { bg: "#f43f5e", border: "#881337", text: "#fff1f2" },  // rose
];
const toneForDay = (dayNum) => DAY_TONES[(dayNum - 1) % DAY_TONES.length];

// ---------------- subcomponents ----------------

function StatusBadge({ status }) {
  if (status === "cancelled") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border border-rose-500/40 bg-rose-500/10 text-rose-200">
        <XCircle className="w-3 h-3" />
        Cancelled
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200">
      <CheckCircle2 className="w-3 h-3" />
      Scheduled
    </span>
  );
}

function CrewCard({ member }) {
  const roleIcon = member.role_on_trip === "driver" ? Truck
    : member.role_on_trip === "lead_host" ? Briefcase
    : UserCheck;
  const Icon = roleIcon;
  const tone =
    member.role_on_trip === "driver"
      ? "border-sky-500/30 bg-sky-500/5"
      : member.role_on_trip === "lead_host"
      ? "border-amber-500/30 bg-amber-500/5"
      : "border-fuchsia-500/30 bg-fuchsia-500/5";
  const roleLabel = {
    driver: "Driver",
    lead_host: "Lead host",
    host: "Host",
  }[member.role_on_trip] || "Crew";
  return (
    <div
      data-testid={`crew-card-${member.user_id}`}
      className={`panel p-3 ${tone} flex items-start gap-3`}
    >
      <div className="shrink-0 w-10 h-10 rounded-full bg-zinc-900 border border-zinc-800 overflow-hidden flex items-center justify-center">
        {member.picture ? (
          <img src={member.picture} alt={member.name} className="w-full h-full object-cover" />
        ) : (
          <Icon className="w-5 h-5 text-zinc-500" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1">
          <Icon className="w-3 h-3" />
          {roleLabel}
        </div>
        <div className="text-sm font-medium text-zinc-100 truncate">
          {member.name}
        </div>
        {member.email && (
          <a
            href={`mailto:${member.email}`}
            className="text-xs text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1 truncate"
            data-testid={`crew-email-${member.user_id}`}
          >
            <Mail className="w-3 h-3 shrink-0" />
            <span className="truncate">{member.email}</span>
          </a>
        )}
      </div>
    </div>
  );
}

function StopRow({ stop, index, dayTone, isLast }) {
  const cancelled = stop.status === "cancelled";
  const completed = stop.status === "completed";
  return (
    <li
      data-testid={`stop-row-${stop.bus_tasting_id}`}
      className="relative pl-10"
    >
      {/* Timeline dot */}
      <div
        className="absolute left-2 top-1 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-mono font-bold shadow"
        style={{
          background: dayTone.bg,
          border: `2px solid ${dayTone.border}`,
          color: dayTone.text,
        }}
      >
        {index + 1}
      </div>
      {!isLast && (
        <div className="absolute left-[18px] top-7 bottom-0 w-px bg-zinc-800" />
      )}

      <Link
        to={`/dispensaries/${stop.dispensary_id}`}
        className="panel p-3 block group hover:border-emerald-500/30 transition-colors"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-zinc-100 group-hover:text-emerald-300 truncate inline-flex items-center gap-1.5">
              {stop.dispensary_nickname || stop.dispensary_name || "Unnamed stop"}
              <ExternalLink className="w-3 h-3 text-zinc-700 group-hover:text-zinc-400" />
              {cancelled && (
                <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-rose-500/40 bg-rose-500/10 text-rose-200">
                  Cancelled
                </span>
              )}
              {completed && (
                <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200">
                  Completed
                </span>
              )}
              {stop.is_extended_stay && (
                <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200">
                  Extended stay
                </span>
              )}
            </div>
            <div className="text-[11px] text-zinc-500 truncate inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {stop.dispensary_city || "—"}
              {stop.dispensary_county ? ` · ${stop.dispensary_county}` : ""}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs font-mono text-emerald-300 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {fmtTime(stop.arrive_at)}
            </div>
            <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
              → {fmtTime(stop.leave_by)} ({fmtMinutes(stop.stop_duration_minutes)})
            </div>
          </div>
        </div>

        {(stop.requested_notes || stop.dispatcher_notes || stop.extended_stay_notes) && (
          <div className="mt-2 text-xs text-zinc-300 leading-snug whitespace-pre-wrap break-words bg-zinc-950/60 border border-zinc-800 rounded p-2 space-y-1">
            {stop.requested_notes && (
              <div>
                <span className="text-zinc-500 font-mono text-[10px] uppercase">Request:</span>{" "}
                {stop.requested_notes}
              </div>
            )}
            {stop.dispatcher_notes && (
              <div>
                <span className="text-zinc-500 font-mono text-[10px] uppercase">Notes:</span>{" "}
                {stop.dispatcher_notes}
              </div>
            )}
            {stop.extended_stay_notes && (
              <div>
                <span className="text-zinc-500 font-mono text-[10px] uppercase">Extended stay:</span>{" "}
                {stop.extended_stay_notes}
              </div>
            )}
          </div>
        )}
      </Link>

      {/* Drive leg to next stop */}
      {stop.travel_minutes_to_next != null && !isLast && (
        <div
          data-testid={`travel-leg-${stop.bus_tasting_id}`}
          className="my-1 ml-2 text-[11px] font-mono text-zinc-500 inline-flex items-center gap-1.5"
        >
          <Truck className="w-3 h-3" />
          Drive {fmtMinutes(stop.travel_minutes_to_next)}
          {stop.travel_live === false && (
            <span className="text-amber-400" title="OSRM unavailable — using haversine fallback estimate">
              · est.
            </span>
          )}
        </div>
      )}
    </li>
  );
}

function DaySection({ day, hasOvernight, isLastDay }) {
  const tone = toneForDay(day.day_number);
  return (
    <section
      data-testid={`trip-day-${day.day_number}`}
      className="space-y-3"
    >
      <div className="flex items-baseline gap-3 flex-wrap">
        <h3 className="text-base font-semibold text-zinc-100 inline-flex items-center gap-2">
          <span
            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-mono font-bold"
            style={{ background: tone.bg, color: tone.text, border: `2px solid ${tone.border}` }}
          >
            {day.day_number}
          </span>
          Day {day.day_number}
        </h3>
        <span className="text-xs text-zinc-500 font-mono">·</span>
        <span className="text-xs text-zinc-400 font-mono inline-flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {fmtDate(day.date)}
        </span>
        <span className="text-xs text-zinc-500 font-mono">·</span>
        <span className="text-xs text-zinc-400 font-mono">
          {day.stops.length} stop{day.stops.length === 1 ? "" : "s"}
        </span>
      </div>

      <ol className="space-y-3">
        {day.stops.map((s, i) => (
          <StopRow
            key={s.bus_tasting_id}
            stop={s}
            index={i}
            dayTone={tone}
            isLast={i === day.stops.length - 1}
          />
        ))}
      </ol>

      {hasOvernight && !isLastDay && (
        <div
          data-testid={`overnight-marker-${day.day_number}`}
          className="ml-10 text-[11px] font-mono text-indigo-300 inline-flex items-center gap-1.5 border border-indigo-500/40 bg-indigo-500/10 rounded px-2 py-1"
        >
          <Moon className="w-3 h-3" />
          Overnight stay after Day {day.day_number}
        </div>
      )}
    </section>
  );
}

function TripMap({ days }) {
  // Flatten all valid-coord stops across all days; collect per-day polylines.
  const dayLines = useMemo(() => {
    return days.map((d) => ({
      day: d.day_number,
      tone: toneForDay(d.day_number),
      coords: d.stops
        .filter((s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude))
        .map((s) => [s.latitude, s.longitude]),
      stops: d.stops.filter((s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude)),
    }));
  }, [days]);

  const allCoords = useMemo(() => dayLines.flatMap((d) => d.coords), [dayLines]);
  const center = allCoords[0] || [36.7783, -119.4179];
  const bounds = allCoords.length ? L.latLngBounds(allCoords).pad(0.2) : null;

  if (allCoords.length === 0) {
    return (
      <div
        data-testid="trip-map-empty"
        className="panel p-8 text-center text-sm text-zinc-500"
      >
        No mappable stops — dispensaries on this trip are missing coordinates.
      </div>
    );
  }

  return (
    <div
      data-testid="trip-map"
      className="panel overflow-hidden"
      style={{ height: 420 }}
    >
      <MapContainer
        center={center}
        zoom={9}
        scrollWheelZoom
        zoomControl={false}
        bounds={bounds || undefined}
        style={{ height: "100%", width: "100%", background: "#9a978f" }}
      >
        <ZoomControl position="topright" />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          subdomains={["a", "b", "c"]}
          maxZoom={19}
        />
        {dayLines.map((d) =>
          d.coords.length > 1 ? (
            <Polyline
              key={`poly-${d.day}`}
              positions={d.coords}
              pathOptions={{ color: d.tone.bg, weight: 4, opacity: 0.75 }}
            />
          ) : null,
        )}
        {dayLines.flatMap((d) =>
          d.stops.map((s, i) => (
            <Marker
              key={s.bus_tasting_id}
              position={[s.latitude, s.longitude]}
              icon={makeNumberedIcon(i + 1, d.tone)}
            />
          )),
        )}
      </MapContainer>
    </div>
  );
}

// ---------------- page ----------------

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await fetchTrip(id);
      setTrip(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        setErr("You are not on this trip's crew.");
      } else if (status === 404) {
        setErr("Trip not found.");
      } else {
        setErr(e?.response?.data?.detail || "Failed to load trip.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onCancel = async () => {
    setActing(true);
    try {
      const res = await cancelTrip(id);
      toast.success(
        `Trip cancelled · ${res.cancelled_bus_events} bus event${res.cancelled_bus_events === 1 ? "" : "s"} cascaded`,
      );
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setActing(false);
    }
  };

  const onUncancel = async () => {
    setActing(true);
    try {
      const res = await uncancelTrip(id);
      toast.success(
        `Trip restored · ${res.restored_bus_events} bus event${res.restored_bus_events === 1 ? "" : "s"} reopened`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setActing(false);
    }
  };

  if (loading && !trip) {
    return (
      <div className="space-y-4" data-testid="trip-detail-loading">
        <Skeleton className="h-10 w-72 bg-zinc-900" />
        <Skeleton className="h-32 w-full bg-zinc-900" />
        <Skeleton className="h-96 w-full bg-zinc-900" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="max-w-xl mx-auto panel p-8 text-center space-y-3" data-testid="trip-detail-error">
        <AlertTriangle className="w-8 h-8 mx-auto text-rose-400" />
        <div className="text-sm text-zinc-300">{err}</div>
        <Link to="/bus-tastings" className="inline-flex items-center gap-1.5 text-sm text-emerald-300 hover:text-emerald-200">
          <ArrowLeft className="w-4 h-4" />
          Back to Bus Events
        </Link>
      </div>
    );
  }

  if (!trip) return null;

  const isCancelled = trip.status === "cancelled";
  const totalStops = (trip.days || []).reduce((n, d) => n + d.stops.length, 0);
  const canCancel = trip.can_cancel && !isCancelled;
  const canUncancel = trip.can_cancel && isCancelled;

  return (
    <div className="space-y-6" data-testid="trip-detail-page">
      {/* ---------------- Header ---------------- */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <button
            onClick={() => navigate(-1)}
            data-testid="trip-back-btn"
            className="text-xs text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1 mb-2 font-mono"
          >
            <ArrowLeft className="w-3 h-3" />
            Back
          </button>
          <h1 className="text-2xl font-semibold text-zinc-50 inline-flex items-center gap-2 flex-wrap">
            <Bus className="w-6 h-6 text-violet-400" />
            <span data-testid="trip-name">{trip.name}</span>
            <StatusBadge status={trip.status} />
          </h1>
          <div className="mt-1 text-sm text-zinc-400 inline-flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {dateRangeLabel(trip.start_date, trip.end_date)}
            </span>
            <span className="text-zinc-700">·</span>
            <span>
              {(trip.days || []).length} day{(trip.days || []).length === 1 ? "" : "s"}
            </span>
            <span className="text-zinc-700">·</span>
            <span>
              {totalStops} stop{totalStops === 1 ? "" : "s"}
            </span>
            {trip.overnight_stays && (
              <>
                <span className="text-zinc-700">·</span>
                <span className="inline-flex items-center gap-1 text-indigo-300">
                  <Moon className="w-3.5 h-3.5" />
                  Overnight stays
                </span>
              </>
            )}
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            data-testid="trip-refresh-btn"
            className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-xs"
          >
            <RefreshCcw className="w-3.5 h-3.5 mr-1" />
            Refresh
          </Button>
          {canUncancel && (
            <Button
              size="sm"
              onClick={onUncancel}
              disabled={acting}
              data-testid="trip-uncancel-btn"
              className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 text-xs"
            >
              {acting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
              Restore trip
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              data-testid="trip-cancel-btn"
              className="border-rose-500/40 bg-rose-500/5 hover:bg-rose-500/15 text-rose-200 text-xs"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Cancel entire trip
            </Button>
          )}
        </div>
      </div>

      {isCancelled && (
        <div
          data-testid="trip-cancelled-banner"
          className="panel p-3 border-rose-500/40 bg-rose-500/5 text-sm text-rose-200 inline-flex items-center gap-2"
        >
          <XCircle className="w-4 h-4" />
          This trip was cancelled on {trip.cancelled_at ? fmtDate(trip.cancelled_at.slice(0, 10)) : "—"}.
          All non-completed stops were cancelled too. {trip.can_cancel && "Click Restore to reopen."}
        </div>
      )}

      {/* ---------------- Crew ---------------- */}
      <section>
        <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-500 mb-2 inline-flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" />
          Crew
          <span className="text-zinc-700">·</span>
          <span>{(trip.crew || []).length}</span>
        </h2>
        {(trip.crew || []).length === 0 ? (
          <div className="panel p-4 text-sm text-zinc-500" data-testid="trip-crew-empty">
            No crew assigned to this trip.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="trip-crew">
            {trip.crew.map((m) => (
              <CrewCard key={m.user_id} member={m} />
            ))}
          </div>
        )}
      </section>

      {/* ---------------- Map ---------------- */}
      <section>
        <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-500 mb-2 inline-flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5" />
          Route map
        </h2>
        <TripMap days={trip.days || []} />
      </section>

      {/* ---------------- Timeline ---------------- */}
      <section className="space-y-6">
        <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          Timeline
        </h2>
        {(trip.days || []).map((d, i) => (
          <DaySection
            key={d.day_number}
            day={d}
            hasOvernight={!!trip.overnight_stays}
            isLastDay={i === (trip.days.length - 1)}
          />
        ))}
      </section>

      {(trip.dispatcher_notes || trip.overnight_notes) && (
        <section className="space-y-2">
          <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" />
            Dispatcher notes
          </h2>
          <div className="panel p-3 text-sm text-zinc-200 whitespace-pre-wrap space-y-2">
            {trip.dispatcher_notes && <div>{trip.dispatcher_notes}</div>}
            {trip.overnight_notes && (
              <div className="text-indigo-200 inline-flex items-start gap-1.5">
                <Moon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{trip.overnight_notes}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ---------------- Cancel confirm ---------------- */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="trip-cancel-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel entire trip?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel <b>{trip.name}</b> and cascade to every scheduled
              stop in it. Completed stops are preserved for audit history. You can
              restore the trip later from this page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trip-cancel-abort">Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={onCancel}
              disabled={acting}
              data-testid="trip-cancel-confirm-btn"
              className="bg-rose-600 hover:bg-rose-500 text-rose-50"
            >
              {acting && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
              Yes, cancel trip
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
